import axios from 'axios';
import { getConfiguredApiUrl } from '../config/runtimeConfig';

export const getApiBaseUrl = () => {
  const envUrl = getConfiguredApiUrl();
  if (envUrl) {
    return envUrl.replace(/\/?$/, '');
  }

  const { protocol, hostname, port } = window.location;
  const origin = `${protocol}//${hostname}${port ? `:${port}` : ''}`;

  // local CRA dev uses proxy or same origin; backend is usually under /api
  return `${origin}/api`;
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshPromise: Promise<string | null> | null = null;

const clearSessionAndRedirect = () => {
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('refreshToken');
  sessionStorage.removeItem('user');
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  window.location.href = '/login';
};

const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = sessionStorage.getItem('refreshToken') || localStorage.getItem('refreshToken');
  if (!refreshToken) return null;

  try {
    const response = await axios.post<{
      token: string;
      refreshToken: string;
      refreshTokenExpiresAt: string;
    }>(`${API_BASE_URL}/auth/refresh-token`, { refreshToken }, {
      headers: { 'Content-Type': 'application/json' },
    });

    const { token, refreshToken: nextRefreshToken, refreshTokenExpiresAt } = response.data;
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('refreshToken', nextRefreshToken);
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', nextRefreshToken);

    const userStr = sessionStorage.getItem('user') || localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        const nextUser = {
          ...user,
          token,
          refreshToken: nextRefreshToken,
          refreshTokenExpiresAt,
        };
        sessionStorage.setItem('user', JSON.stringify(nextUser));
        localStorage.setItem('user', JSON.stringify(nextUser));
      } catch {}
    }

    return token;
  } catch {
    return null;
  }
};

// Request interceptor to attach JWT token
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Attach tenantId and organizationId from user (if available)
    const userStr = sessionStorage.getItem('user') || localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.tenantId) {
          config.headers['X-Tenant-Id'] = user.tenantId;
        }
        if (user.organizationId) {
          config.headers['X-Organization-Id'] = user.organizationId;
        }
      } catch {}
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const requestUrl = String(error.config?.url || '');
    const isLoginRequest = /\/auth\/login$/i.test(requestUrl);
    const isRefreshRequest = /\/auth\/refresh-token$/i.test(requestUrl);

    if (error.response?.status === 401 && !isLoginRequest && !isRefreshRequest && !error.config?._retry) {
      error.config._retry = true;
      refreshPromise ??= refreshAccessToken().finally(() => {
        refreshPromise = null;
      });

      const newToken = await refreshPromise;
      if (newToken) {
        error.config.headers = error.config.headers ?? {};
        error.config.headers.Authorization = `Bearer ${newToken}`;
        return api(error.config);
      }

      clearSessionAndRedirect();
    }
    return Promise.reject(error);
  }
);

export default api;
